import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { candidateMatchStateMessage, NO_SAFE_MATCHES_MESSAGE } from "@jobreel/shared";
import { T } from "../tokens";
import { Btn } from "./Btn";
import { LoadingState } from "./LoadingState";
import { useCandidateReadiness, useGenerateMatches, useEnsureFirstMatchGeneration, type EnsureFirstMatchResult } from "../hooks/useMatches";

/** Replaces a single generic "No matches" message with the ACTUAL reason —
 *  ambiguous target role, matching temporarily disabled, thin job inventory
 *  for the candidate's family, or (after the automatic first attempt
 *  genuinely found nothing) no safe matches. Shared by Dashboard.tsx and
 *  Matches.tsx so both read the same GET /matches/readiness signal instead of
 *  duplicating the mapping. Renders nothing when matches already exist (the
 *  caller shows those).
 *
 *  The FIRST match set requires no click: whenever readiness reports
 *  ready_to_generate, this component fires ensureFirstMatchGeneration itself
 *  (exactly once per mount — a ref guards against React re-render/Strict Mode
 *  double-invoke) and shows a loading message while it runs. The server side
 *  is ALSO idempotent (re-checks at call time whether a current match set
 *  already exists for this résumé), so a page refresh/login/readiness poll
 *  that re-mounts this component and re-fires the effect is still safe — it
 *  just no-ops once matches exist, which readiness itself then reflects
 *  (state flips to matches_available and this component renders nothing). */
export function MatchReadinessPanel() {
  const readiness = useCandidateReadiness();
  const ensureFirst = useEnsureFirstMatchGeneration();
  const refresh      = useGenerateMatches();
  // Client-local only — there is no persisted "we already tried and found
  // nothing" flag (see candidate-match-state.ts's header). Reset whenever the
  // server-derived state changes underneath it (e.g. a role edit resolved).
  const [triedNoMatches, setTriedNoMatches] = useState(false);
  // hardening/resume-ux P0-4: the auto-fire mutation can also resolve with
  // "no_embedding" (resume has no embedding yet) or "empty_pool" (no eligible
  // jobs right now) or "disabled" — none of those is "no_matches", so the old
  // code left triedNoMatches false for all three. Readiness itself has no
  // state for "we tried and couldn't even attempt it" (see the gap this
  // flag exists to paper over), so after invalidation it keeps reporting
  // ready_to_generate, and since autoFired.current stays true the effect
  // never re-fires — the panel was stuck on "Finding your best matches…"
  // forever even though the one attempt had already finished. Tracked
  // separately from triedNoMatches because it is NOT the same situation as a
  // genuine "no safe matches" outcome and must not borrow that message.
  const [autoFirstStalled, setAutoFirstStalled] = useState(false);
  const autoFired = useRef(false);

  const state = readiness.data?.state;

  function handleEnsureFirstResult(data: EnsureFirstMatchResult) {
    if (data.ran && data.status === "no_matches") { setTriedNoMatches(true); return; }
    if (data.ran && data.status !== "populated") { setAutoFirstStalled(true); return; }
  }

  useEffect(() => {
    if (state !== "ready_to_generate" || autoFired.current) return;
    autoFired.current = true;
    ensureFirst.mutate(undefined, {
      onSuccess: handleEnsureFirstResult,
      // A genuine failure should be retryable — allow the effect to fire again.
      onError: () => { autoFired.current = false; },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (readiness.isLoading) return <LoadingState label="Checking your match readiness…" />;
  if (readiness.isError || !readiness.data) return null;   // fail open — caller's existing fallback still applies

  const { matchCount } = readiness.data;
  if (state === "matches_available" && !triedNoMatches) return null;

  // First run: always the loading message while the automatic attempt is in
  // flight (or about to fire) — never a button. Once it resolves, either
  // readiness flips to matches_available (this panel renders nothing), it
  // genuinely found nothing (triedNoMatches, handled below), or it couldn't
  // even attempt it (autoFirstStalled — shown here with a retry, same as a
  // thrown error, rather than silently spinning forever).
  if (state === "ready_to_generate" && !triedNoMatches && !autoFirstStalled) {
    const message = candidateMatchStateMessage("ready_to_generate");
    return (
      <div style={{
        background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px",
        padding: "32px", textAlign: "center", marginBottom: "12px",
      }}>
        <p style={{ fontFamily: T.display, fontSize: "16px", color: T.t1, margin: "0 0 6px" }}>
          {message.headline}
        </p>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0, maxWidth: "440px", marginInline: "auto" }}>
          {message.body}
        </p>
        {ensureFirst.isError && (
          <>
            <p role="alert" style={{ fontFamily: T.body, fontSize: "12px", color: T.err, margin: "10px 0 0" }}>
              Could not find your matches right now.
            </p>
            <Btn
              variant="ghost" size="sm"
              disabled={ensureFirst.isPending}
              onClick={() => ensureFirst.mutate(undefined, { onSuccess: handleEnsureFirstResult })}
              style={{ marginTop: "8px" }}
            >
              Try again
            </Btn>
          </>
        )}
      </div>
    );
  }

  // hardening/resume-ux P0-4: the automatic first attempt resolved but
  // couldn't actually produce a verdict (no embedding yet, no eligible jobs
  // right now, or matching temporarily disabled) — honest "couldn't check"
  // copy with a retry, same shape as the thrown-error case above, rather
  // than an indefinite "Finding your best matches…".
  if (autoFirstStalled && !triedNoMatches) {
    return (
      <div style={{
        background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px",
        padding: "32px", textAlign: "center", marginBottom: "12px",
      }}>
        <p style={{ fontFamily: T.display, fontSize: "16px", color: T.t1, margin: "0 0 6px" }}>
          Could not check your matches
        </p>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0, maxWidth: "440px", marginInline: "auto" }}>
          Something kept us from generating your matches just now. This usually resolves on its own — try again in a moment.
        </p>
        <Btn
          variant="ghost" size="sm"
          disabled={ensureFirst.isPending}
          onClick={() => {
            setAutoFirstStalled(false);
            ensureFirst.mutate(undefined, { onSuccess: handleEnsureFirstResult });
          }}
          style={{ marginTop: "12px" }}
        >
          Try again
        </Btn>
      </div>
    );
  }

  const message = triedNoMatches ? NO_SAFE_MATCHES_MESSAGE : candidateMatchStateMessage(state!);

  return (
    <div style={{
      background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px",
      padding: "32px", textAlign: "center", marginBottom: "12px",
    }}>
      <p style={{ fontFamily: T.display, fontSize: "16px", color: T.t1, margin: "0 0 6px" }}>
        {message.headline}
      </p>
      <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: "0 0 16px", maxWidth: "440px", marginInline: "auto" }}>
        {message.body}
      </p>
      {message.showGenerateAction && (
        <>
          <Btn
            variant="cta" size="md"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate(undefined, {
              onSuccess: (data) => setTriedNoMatches(data.status === "no_matches"),
            })}
          >
            {refresh.isPending ? "Refreshing…" : "Refresh Matches"}
          </Btn>
          {refresh.isError && (
            <p role="alert" style={{ fontFamily: T.body, fontSize: "12px", color: T.err, margin: "10px 0 0" }}>
              {axios.isAxiosError(refresh.error) && refresh.error.response?.status === 429
                ? "You've reached the match-generation limit for now. Please try again later."
                : "Could not check for matches right now. Please try again."}
            </p>
          )}
        </>
      )}
      {matchCount === 0 && state === "matches_available" && (
        // Defensive — state derivation guarantees matchCount>0 for this state,
        // but never silently show an empty "matches available" panel if the
        // signals ever disagree.
        <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, margin: "10px 0 0" }}>
          Refresh the page if this looks out of date.
        </p>
      )}
    </div>
  );
}
